import * as React from "react";
import JobStatus from "../../../model/job/JobStatus";
import * as classNames from "classnames";
import {DragDropContext, Draggable, DragUpdate, Droppable} from "react-beautiful-dnd";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import Toast from "../../helper/Toast";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import {connect} from "react-redux";
import {changeJobStatusSuspend} from "../../../store/action/jobStatus/jobStatus";
import {Dispatch} from "redux";
import {arrayMove} from "../../../utils/ArrayUtils";

interface ComponentProps {
	/**
	 * Job statuses.
	 */
	jobStatuses: JobStatus[];
	/**
	 * Row click callback
	 */
	onRowClick: (jobStatus: JobStatus) => void;
	/**
	 * Order change callback.
	 * @param {JobStatus[]} items
	 */
	onOrderChange: (jobStatuses: JobStatus[]) => void;
}

interface ReduxProps {
	/**
	 * Disable or enable job status
	 * @param {string} jobStatusId
	 * @param {boolean} disabled
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	changeStatusSuspend: (jobStatusId: string, disabled: boolean, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

type Props = ComponentProps & ReduxProps;

class JobStatusesTable extends React.Component<Props, {}> {
	constructor(props: Props) {
		super(props);
		this.onDragEnd = this.onDragEnd.bind(this);
		this.enableStatus = this.enableStatus.bind(this);
		this.disableStatus = this.disableStatus.bind(this);
	}

	render() {
		return (
			<DragDropContext onDragEnd={this.onDragEnd}>
				<Droppable droppableId="droppable">
					{provided => (
						<div ref={provided.innerRef}>
							<table style={{tableLayout: "auto"}} className="l-table-default table-hover">
								<thead>
								<tr>
									<th>Id</th>
									<th>Label</th>
									<th>Category</th>
									<th>Description</th>
									<th>Actions</th>
								</tr>
								</thead>
								<tbody style={{tableLayout: "auto"}}>
								{this.props.jobStatuses.map((item, index) => (
									<Draggable key={item.id} draggableId={item.id} index={index}>
										{(provided, snapshot) => (
											<tr onClick={() => this.props.onRowClick(item)}
												ref={provided.innerRef}
												{...provided.draggableProps}
												{...provided.dragHandleProps}
												className={classNames({
													"c-job-status-list-item": true,
													"isDragging": Boolean(snapshot.isDragging),
													"isDisabled": item.disabled
												})}
												style={{...provided.draggableProps.style}}>
												<td style={{width: "20%"}}>{item.id}</td>
												<td style={{width: "10%"}}>{item.label}</td>
												<td style={{width: "10%"}}>{item.category}</td>
												<td style={{width: "50%"}}>{item.description}</td>
												<td style={{width: "10%"}}>{item.disabled ?
													<ButtonPrimary label={"Enable"} onClick={() => this.enableStatus(item.id)}/> :
													<ButtonPrimary label={"Disable"}
																   onClick={() => this.disableStatus(item.id)}/>}
												</td>
											</tr>
										)}
									</Draggable>
								))}
								</tbody>
							</table>
						</div>
					)}
				</Droppable>
			</DragDropContext>
		)
	}

	/**
	 * Call back on stop dragging action.
	 * @param result
	 */
	onDragEnd(result: DragUpdate) {
		// dropped outside the list
		if (!result.destination) {
			return;
		}
		this.props.onOrderChange(arrayMove(this.props.jobStatuses, result.source.index, result.destination.index, false));
	}

	enableStatus(jobStatusId: string) {
		this.props.changeStatusSuspend(jobStatusId, false,
			() => {Toast.success("Status enabled", "Job status enable")},
			() => {Toast.error("Sorry, something went wrong. Try again later.", "Job status enable")})
	}

	disableStatus(jobStatusId: string) {
		this.props.changeStatusSuspend(jobStatusId, true,
			() => {Toast.success("Status disabled", "Job status disable")},
			(error: any) => {
				let err: string;
				if (error && error.response && (error.response.status === 412))
					switch (error.response.data.exception) {
						case "DisableJobStatusNotAllowedException" :
							err = "This status can not be disabled";
							break;
						case "DisableAllJobStatusesNotAllowedException":
							err = "Cannot disable all job statuses. At least one must be enabled.";
							break;
						default:
							err = "Sorry, something went wrong. Try again later."
					}
				else
					err = "Sorry, something went wrong. Try again later.";
				Toast.error(err, "Job status disable");
			})
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		changeStatusSuspend: (jobStatusId: string, disabled: boolean, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			changeJobStatusSuspend(jobStatusId, disabled, onSuccess, onFailure)
		)
	}
}

export default connect(null, mapDispatchToProps)(JobStatusesTable);
